import React from 'react';
import { ClipboardList, Mail, SquarePlay, type LucideIcon } from 'lucide-react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  OperateTabId,
  operateTabNavigationPath,
  resolveOperateDomain,
  resolveOperateTab,
} from './projectPhases';
import './wideTabs.css';

const SECONDARY_ICONS: Partial<Record<OperateTabId, LucideIcon>> = {
  incidents: Mail,
  'runtime-logs': SquarePlay,
  reported: ClipboardList,
};

/** In-domain secondary views as full-width tabs with a sliding underline. Arrow keys move between views. */
export const OperateDomainSecondaryNav: React.FC = () => {
  const { projectId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const incidentId = searchParams.get('incident');
  const requestId = searchParams.get('request');

  const activeTab = resolveOperateTab(location.pathname);
  const domain = resolveOperateDomain(activeTab);
  if (!domain.secondary.length) return null;

  const active = domain.secondary.some((item) => item.id === activeTab) ? activeTab : domain.secondary[0].id;

  const activeIndex = domain.secondary.findIndex((item) => item.id === active);
  const indicatorVars = { '--n': domain.secondary.length, '--i': activeIndex } as React.CSSProperties;

  const goTo = (id: OperateTabId) =>
    navigate(
      operateTabNavigationPath(projectId, id, {
        incidentId: id === 'runtime-logs' || id === 'reported' ? null : incidentId,
        requestId,
      }),
    );

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const tabs = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
    const from = tabs.indexOf(document.activeElement as HTMLButtonElement);
    const next = (Math.max(from, 0) + step + tabs.length) % tabs.length;
    tabs[next]?.focus();
    goTo(domain.secondary[next].id);
  };

  return (
    <div
      className="wtabs"
      role="tablist"
      aria-label={`${domain.label} views`}
      onKeyDown={onKeyDown}
      style={indicatorVars}
    >
      <span className="wt-ind" aria-hidden />
      {domain.secondary.map((item) => {
        const Icon = SECONDARY_ICONS[item.id];
        const on = item.id === active;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            className="wt"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            title={item.description}
            onClick={() => goTo(item.id)}
          >
            {Icon ? <Icon strokeWidth={1.8} aria-hidden /> : null}
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
};

/** @deprecated Domains live in PhaseContextNav — kept for legacy imports */
const OperateSubNav: React.FC<{ embedded?: boolean }> = () => null;

export default OperateSubNav;
